import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
    /* Existing styles */
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7); /* Semi-transparent background */
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const Modal = styled.div`
    /* Existing styles */
    background-color: ${({ theme }) => theme.modalBackground || '#808080'};
    color: ${({ theme }) => theme.modalTextColor || '#ffffff'};
    padding: 32px;
    border-radius: 8px;
    max-width: 500px;
    width: 90%;
    position: relative;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    max-height: 80vh;
`;

const Header = styled.div`
    /* Existing styles */
    background-color: ${({ theme }) => theme.modalHeaderBackground || '#606060'};
    color: ${({ theme }) => theme.modalHeaderTextColor || '#ffffff'};
    padding: 16px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    margin: -32px -32px 24px -32px;
    text-align: center;
    font-size: 1.5rem;
    font-weight: bold;
`;

const CloseButton = styled.button`
    /* Existing styles */
    position: absolute;
    top: 12px;
    right: 12px;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: ${({ theme }) => theme.modalTextColor || '#ffffff'};

    &:hover {
        color: ${({ theme }) => theme.accentColor || '#538d4e'};
    }
`;

const SettingItem = styled.div`
    /* Existing styles */
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const SettingLabel = styled.span`
    font-size: 1rem;
    color: ${({ theme }) => theme.modalTextColor || '#ffffff'};
`;

const SettingToggle = styled.input`
    /* Existing styles */
    width: 40px;
    height: 20px;
    appearance: none;
    background-color: ${({ theme }) => theme.borderColor || '#ccc'};
    border-radius: 10px;
    position: relative;
    outline: none;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:checked {
        background-color: ${({ theme }) => theme.accentColor || '#538d4e'};
    }

    &:before {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 16px;
        height: 16px;
        background-color: ${({ theme }) => theme.modalTextColor || '#ffffff'};
        border-radius: 50%;
        transition: transform 0.2s ease;
    }

    &:checked:before {
        transform: translateX(20px);
    }
`;

const SettingLink = styled.a`
    color: ${({ theme }) => theme.accentColor || '#538d4e'};
    text-decoration: none;
    font-size: 1rem;

    &:hover {
        text-decoration: underline;
    }
`;

interface SettingsModalProps {
    toggleTheme: () => void;
    onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
                                                         toggleTheme,
                                                         onClose,
                                                     }) => {
    const [darkTheme, setDarkTheme] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        setDarkTheme(savedTheme === 'dark');

        // Disable background scrolling when modal is open
        document.body.style.overflow = 'hidden';

        return () => {
            // Re-enable scrolling when modal is closed
            document.body.style.overflow = 'auto';
        };
    }, []);

    const handleDarkThemeToggle = () => {
        setDarkTheme(!darkTheme);
        toggleTheme();
    };

   
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    return (
        <Overlay>
            <Modal ref={modalRef}>
                <Header>Settings</Header>
                <CloseButton onClick={onClose} aria-label="Close">
                    &times;
                </CloseButton>
                <SettingItem>
                    <SettingLabel>Dark Theme</SettingLabel>
                    <SettingToggle
                        type="checkbox"
                        checked={darkTheme}
                        onChange={handleDarkThemeToggle}
                        aria-label="Toggle Dark Theme"
                    />
                </SettingItem>
                <SettingItem>
                    <SettingLabel>Feedback</SettingLabel>
                    <SettingLink href="mailto:oysterbaygames@gmail.com?subject=[Riddle Ravel Feedback]">
                        Email
                    </SettingLink>
                </SettingItem>
                <SettingItem>
                    <SettingLabel>Report a Bug</SettingLabel>
                    <SettingLink href="mailto:oysterbaygames@gmail.com?subject=[Riddle Ravel Bug Report]">
                        Email
                    </SettingLink>
                </SettingItem>
            </Modal>
        </Overlay>
    );
};

export default SettingsModal;
